<template>
  <div
    v-if="activeTimer"
    class="design-job-timer-toast fixed bottom-4 right-4 z-50 bg-surface border border-border rounded-lg shadow-lg p-4 min-w-[280px] max-w-[320px]"
  >
    <div class="space-y-3">
      <!-- Job Title and Open Button -->
      <div class="flex items-center justify-between gap-2">
        <BaseText size="medium" class="text-text font-medium truncate flex-1">
          {{ activeTimer.jobTitle }}
        </BaseText>
        <BaseButton
          variant="secondary"
          size="xs"
          @click="handleOpenJob"
        >
          Open
        </BaseButton>
      </div>

      <!-- Timer Display -->
      <div class="text-center">
        <div class="text-2xl font-mono font-semibold text-text">
          {{ formatTime(totalTrackedSeconds) }}
        </div>
      </div>

      <!-- Controls -->
      <div v-if="activeTimer" class="flex items-center justify-center gap-2">
        <!-- Play/Resume (if paused) -->
        <BaseButton
          v-if="activeTimer.timer.status === 'paused'"
          variant="primary"
          size="small"
          :disabled="timerStore.isLoading"
          @click="handleResume"
        >
          <BaseIcon size="medium">
            <PlayIcon />
          </BaseIcon>
        </BaseButton>

        <!-- Pause (if running) -->
        <BaseButton
          v-if="activeTimer.timer.status === 'running'"
          variant="warning"
          size="small"
          class="h-8 w-12"
          :disabled="timerStore.isLoading"
          @click="handlePause"
        >
          <BaseIcon size="medium">
            <PauseIcon />
          </BaseIcon>
        </BaseButton>

        <!-- Stop (always available when session exists) -->
        <BaseButton
          variant="danger"
          size="small"
          class="h-8 w-12"
          :disabled="timerStore.isLoading"
          @click="handleStop"
        >
          <BaseIcon size="large">
            <StopIcon />
          </BaseIcon>
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDesignJobTimerStore } from '@/stores/designJobTimer'
import BaseText from '@/components/ui/BaseText.vue'
import BaseButton from '@/components/navigation/BaseButton.vue'
import BaseIcon from '@/components/ui/BaseIcon.vue'
import PlayIcon from '@/components/icons/PlayIcon.vue'
import PauseIcon from '@/components/icons/PauseIcon.vue'
import StopIcon from '@/components/icons/StopIcon.vue'

const route = useRoute()
const router = useRouter()
const timerStore = useDesignJobTimerStore()

const activeTimer = computed(() => {
  // Only show toast if we have an active timer that is RUNNING (not paused/stopped) AND we're not on the detail page
  const isOnDetailPage = route.name === 'DesignJobDetail' && 
                         route.params.uuid === timerStore.activeTimer?.jobUuid
  if (isOnDetailPage) {
    return null
  }
  
  // Only show toast if timer is running (not paused or stopped)
  const timer = timerStore.activeTimer
  if (!timer || timer.timer.status !== 'running') {
    return null
  }
  
  return timer
})

const totalTrackedSeconds = computed(() => {
  if (!activeTimer.value) return 0
  const baseTotal = activeTimer.value.total_tracked_seconds || 0
  if (activeTimer.value.timer.status === 'running') {
    return baseTotal + timerStore.currentSessionSeconds
  }
  return baseTotal
})

// Timer interval for real-time updates
let timerInterval: number | null = null

const forceUpdate = () => {
  // Tick the store to force reactivity in computed properties
  timerStore.tick()
}

// Watch for active timer changes
watch(activeTimer, (newTimer) => {
  if (newTimer && newTimer.timer.status === 'running') {
    if (!timerInterval) {
      timerInterval = window.setInterval(() => {
        forceUpdate()
      }, 1000)
    }
  } else {
    if (timerInterval) {
      clearInterval(timerInterval)
      timerInterval = null
    }
  }
}, { immediate: true })

// Watch timer status
watch(() => activeTimer.value?.timer.status, (newStatus) => {
  if (newStatus === 'running') {
    if (!timerInterval) {
      timerInterval = window.setInterval(() => {
        forceUpdate()
      }, 1000)
    }
  } else {
    if (timerInterval) {
      clearInterval(timerInterval)
      timerInterval = null
    }
  }
})

// Format time helper
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

// Handlers
const handleOpenJob = () => {
  if (activeTimer.value) {
    router.push(`/design-jobs/${activeTimer.value.jobUuid}`)
  }
}

const handleResume = () => {
  timerStore.resumeTimer()
}

const handlePause = () => {
  // Pause in Toast: pauses timer but toast stays visible
  timerStore.pauseTimer()
}

const handleStop = () => {
  // Stop in Toast: stops timer and toast will disappear (via computed)
  timerStore.stopTimer('manual')
}

// Cleanup
onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
})
</script>

<style scoped>
.design-job-timer-toast {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
</style>

